/**
 * Document Export Utilities
 * Handles downloading/exporting documents in various formats
 */

/**
 * Convert HTML content to plain text
 */
export const htmlToPlainText = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

/**
 * Build Word-compatible HTML string (for sending to server as JSON)
 */
export const createWordDocumentHtml = (content) => {
  const html = typeof content === 'string' ? content : String(content || '');
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="ProgId" content="Word.Document">
<style>
body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; margin: 1in; line-height: 1.5; }
h1, h2, h3 { color: #003366; margin-top: 12pt; margin-bottom: 6pt; }
p { margin-bottom: 6pt; }
table { border-collapse: collapse; margin: 10pt 0; }
td, th { border: 1px solid #999; padding: 4pt; }
</style>
</head>
<body>
${html}
</body>
</html>`;
};

/**
 * Create a Word document from HTML content
 * Returns a Blob that can be downloaded as .doc/.docx
 */
export const createWordDocument = (content, fileName) => {
  return new Blob([createWordDocumentHtml(content)], { type: 'application/msword' });
};

/**
 * Download a blob as a file
 */
export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export document with format selection
 * Returns format selection object to use in modal/dialog
 */
export const getExportFormats = () => [
  { 
    id: 'docx', 
    label: 'Word (.docx)', 
    extension: '.docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  },
  { 
    id: 'doc', 
    label: 'Word (.doc)', 
    extension: '.doc',
    mimeType: 'application/msword'
  },
  { 
    id: 'txt', 
    label: 'Text (.txt)', 
    extension: '.txt',
    mimeType: 'text/plain'
  },
];

/**
 * Convert HTML content to the requested format
 */
export const convertContent = (html, format) => {
  switch (format) {
    case 'txt':
      return {
        blob: new Blob([htmlToPlainText(html)], { type: 'text/plain;charset=utf-8' }),
        extension: '.txt'
      };
    case 'doc':
    case 'docx':
      return {
        blob: createWordDocument(html, 'document'),
        extension: format === 'docx' ? '.docx' : '.doc'
      };
    default:
      return {
        blob: createWordDocument(html, 'document'),
        extension: '.docx'
      };
  }
};
